// Fetch wrapper for the deck engine (FastAPI behind the Shopify App Proxy).
// - every call has a timeout (AbortController)
// - network failure / timeout / 5xx => ApiError{offline:true}; pages switch to their offline state
// - engine status is broadcast as a window event 'mcc:engine' {detail:{online:bool}}
// - GETs are cached in memory for a short time (search-as-you-type, card sheets)

import { config } from './singles-hub--config.js?v=c7c57de095';
import { basketMap, basketParam } from './singles-hub--stock.js?v=c7c57de095';
import { cart } from './singles-hub--cart.js?v=c7c57de095';

export class ApiError extends Error {
  constructor(msg, { status = 0, offline = false, body = null } = {}) {
    super(msg);
    this.status = status;
    this.offline = offline;
    this.body = body;
  }
}

let online = true;
export const isOnline = () => online;
function setOnline(v) {
  if (v === online) return;
  online = v;
  try { window.dispatchEvent(new CustomEvent('mcc:engine', { detail: { online: v } })); } catch (e) { /* ignore */ }
}

const cache = new Map(); // url -> {t, data}
const CACHE_MS = 30000;

// Two mounts on one page (deck header + deck page) ask for the same GET at once: share the in-flight request.
const inflight = new Map(); // key -> Promise
function request(method, path, opts = {}) {
  if (method !== 'GET' || opts.cacheable === false) return send(method, path, opts);
  const key = path + '|' + JSON.stringify(opts.query || {});
  if (inflight.has(key)) return inflight.get(key);
  const p = send(method, path, opts).finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

async function send(method, path, { query, body, timeout, cacheable } = {}) {
  const cfg = config();
  let url = cfg.apiBase + path;
  if (query) {
    const q = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '' || v === false) return;
      if (Array.isArray(v)) { if (v.length) q.set(k, v.join(',')); } else q.set(k, v === true ? '1' : String(v));
    });
    const qs = q.toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }
  if (method === 'GET' && cacheable !== false) {
    const hit = cache.get(url);
    if (hit && Date.now() - hit.t < CACHE_MS) return hit.data;
  }
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeout || cfg.timeoutMs);
  let res = null;
  // the theme section starts the hub's first GETs before the modules load (window.__mccPre[url]); use them once
  const pre = method === 'GET' ? takePrefetch(url) : null;
  if (pre) { try { res = await Promise.race([pre, new Promise((r) => ctl.signal.addEventListener('abort', () => r(null)))]); } catch (e) { res = null; } }
  if (!res) try {
    res = await fetch(url, {
      method,
      headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      signal: ctl.signal,
      credentials: 'same-origin',
    });
  } catch (err) {
    clearTimeout(timer);
    setOnline(false);
    throw new ApiError(err && err.name === 'AbortError' ? 'Timed out' : 'Network error', { offline: true });
  }
  // keep the abort timer running until the BODY is read: a stalled body must still time out
  let data = null;
  let text;
  try {
    text = await res.text();
  } catch (err) {
    setOnline(false);
    throw new ApiError(err && err.name === 'AbortError' ? 'Timed out' : 'Network error', { offline: true });
  } finally {
    clearTimeout(timer);
  }
  try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
  if (res.status >= 500 || (res.ok && data === null && text)) {
    setOnline(false);
    throw new ApiError('Engine error ' + res.status, { status: res.status, offline: true, body: data });
  }
  setOnline(true);
  if (!res.ok) throw new ApiError((data && (data.detail || data.message)) || 'Request failed', { status: res.status, body: data });
  if (method === 'GET' && cacheable !== false) cache.set(url, { t: Date.now(), data });
  return data;
}

export const clearCache = () => cache.clear();

function takePrefetch(url) {
  const p = window.__mccPre;
  if (!p || !p[url]) return null;
  const r = p[url];
  delete p[url];
  return r;
}

// ---------------------------------------------------------------- endpoints
// Stock-bearing calls carry the whole basket (FEEDBACK_PLAN §1): GET as basket=vid:qty,..., POST as {basket:{vid:qty}}.
// the basket is read once (from /cart.js) before the first stock-bearing call, so nothing is ever shown as
// available that is already in the basket (e.g. a ?card= sheet opened on page load)
let basketReady = null;
const ready = () => (cart.last() ? Promise.resolve() : (basketReady = basketReady || cart.get().catch(() => null)));
const withB = async (q) => { await ready(); return Object.assign({}, q || {}, { basket: basketParam() }); };
const withBody = async (b) => { await ready(); return Object.assign({ basket: basketMap() }, b || {}); };
export const api = {
  health: () => request('GET', '/healthz', { cacheable: false, timeout: 4000 }),
  parse: (text) => request('POST', '/parse', { body: { text } }),
  // body: {lines|deck_id, mode, owned, overrides, swaps, accepted, reserved} (+ basket)
  resolve: async (body) => request('POST', '/resolve', { body: await withBody(body) }),
  // Same body as resolve; returns {text, total, skipped:[raw lines]}
  exportList: (body) => request('POST', '/export', { body }),
  revalidate: (items) => request('POST', '/revalidate', { body: { items } }),
  search: async (params) => request('GET', '/search', { query: await withB(params) }),
  suggest: async (q) => request('GET', '/suggest', { query: await withB({ q }), timeout: 4000 }),
  card: async (id) => request('GET', '/card/' + encodeURIComponent(id), { query: await withB() }),
  decks: async (params) => request('GET', '/decks', { query: await withB(params) }),
  deck: async (slug) => request('GET', '/decks/' + encodeURIComponent(slug), { query: await withB() }),
  lists: async (params) => request('GET', '/lists', { query: await withB(params) }),
  list: async (id) => request('GET', '/lists/' + encodeURIComponent(id), { query: await withB() }),
  kit: async (params) => request('GET', '/kit', { query: await withB(params) }),
  staples: async (params) => request('GET', '/staples', { query: await withB(params) }),
  // up to 6 Standard-legal cards with >=1 copy available, ranked by deck usage (operator note #1)
  // lite: names + stock only (the hub chips and search use nothing else)
  popular: async () => request('GET', '/popular', { query: await withB({ lite: 1 }), timeout: 4000 }),
  // basket-wide checks (Q2): lines [{variant_id, qty, deck_id?}] -> {warnings:[...]}
  basketCheck: (lines) => request('POST', '/basket/check', { body: { lines }, timeout: 4000 }),
  sets: () => request('GET', '/sets'),
  createRequest: (body) => request('POST', '/requests', { body }),
  getRequests: (params) => request('GET', '/requests', { query: params, cacheable: false }),
  // who = {token} | {customer_id}: the owner check needs it (the App Proxy adds logged_in_customer_id)
  deleteRequest: (id, who) => request('DELETE', '/requests/' + encodeURIComponent(id), { query: who || {} }),
  renewRequest: (id, who) => request('POST', '/requests/' + encodeURIComponent(id) + '/renew', { query: who || {} }),
  requestPrefs: (body) => request('POST', '/requests/prefs', { body }),
};
